/**
 * InviteExternalDrawer — invite an executive / observer / board member /
 * external auditor. Default access_role='viewer' (read-only) with a
 * temporary expiry (30 days).
 *
 * Server auto-picks delivery mode: federated (Azure AD) for federated
 * orgs, magic-link otherwise. Compliance officer can override.
 */
import React, { useState } from 'react';
import HipaaAckBanner from './HipaaAckBanner';

const API = process.env.REACT_APP_API_URL || '';

function thirtyDaysFromNow() {
  const d = new Date(); d.setDate(d.getDate() + 30); return d.toISOString().slice(0, 10);
}

export default function InviteExternalDrawer({ apps, onClose, onSaved }) {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [title, setTitle] = useState('');
  const [appKeys, setAppKeys] = useState([]);
  const [accessRole, setAccessRole] = useState('viewer');
  const [durationMode, setDurationMode] = useState('temporary'); // 'temporary' | 'permanent'
  const [expiresAt, setExpiresAt] = useState(thirtyDaysFromNow());
  const [deliveryMode, setDeliveryMode] = useState('auto');     // 'auto' | 'federated' | 'magic_link'
  const [reason, setReason] = useState('');
  const [hipaaAck, setHipaaAck] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const toggleApp = (k) => {
    setAppKeys(prev => prev.includes(k) ? prev.filter(x => x !== k) : [...prev, k]);
  };

  const submit = async () => {
    setError(null);
    if (!email.trim()) { setError('Email is required.'); return; }
    if (appKeys.length === 0) { setError('Pick at least one app to grant.'); return; }
    if (!hipaaAck) { setError('Please acknowledge the HIPAA notice before granting access.'); return; }
    setBusy(true);
    try {
      const body = {
        email: email.trim(), name: name.trim() || null, title: title.trim() || null,
        app_keys: appKeys,
        access_role: accessRole,
        expires_at: durationMode === 'permanent' ? null : (expiresAt + 'T23:59:59Z'),
        delivery_mode: deliveryMode === 'auto' ? undefined : deliveryMode,
        reason: reason || null,
        hipaa_ack: hipaaAck,
      };
      const r = await fetch(`${API}/api/admin/team/invite-external`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      const j = await r.json();
      if (!r.ok || !j.success) setError(j.message || 'Could not invite.');
      else if (typeof onSaved === 'function') onSaved();
    } catch (e) { setError(e.message || 'Could not invite.'); }
    finally { setBusy(false); }
  };

  return (
    <>
      <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.20)', zIndex: 55 }} />
      <div role="dialog" aria-label="Invite observer" style={{
        position: 'fixed', top: 0, right: 0, bottom: 0, width: 'min(560px, 92vw)',
        background: '#ffffff', borderLeft: '1px solid #cbd5e1', zIndex: 60,
        overflowY: 'auto',
      }}>
        <div style={{ position: 'sticky', top: 0, background: '#ffffff', padding: '16px 20px', borderBottom: '1px solid #e2e8f0', display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', zIndex: 1 }}>
          <div>
            <div style={{ fontSize: 10.5, letterSpacing: '0.08em', textTransform: 'uppercase', color: '#94a3b8', fontFamily: 'ui-monospace, monospace' }}>Invite</div>
            <div style={{ fontSize: 16, color: '#0f172a', fontWeight: 600, marginTop: 4 }}>Observer / executive</div>
          </div>
          <button type="button" onClick={onClose} style={{ background: 'transparent', border: '1px solid #cbd5e1', padding: '4px 10px', fontSize: 12, color: '#475569', cursor: 'pointer', borderRadius: 6 }}>Close</button>
        </div>

        <div style={{ padding: '16px 20px 32px' }}>
          <label style={{ fontSize: 11, color: '#475569', display: 'block', marginBottom: 3 }}>Email</label>
          <input value={email} onChange={(e) => setEmail(e.target.value)} type="email" autoFocus
            style={{ width: '100%', padding: '8px 10px', fontSize: 13, border: '1px solid #cbd5e1', borderRadius: 6, marginBottom: 12 }} />

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <div>
              <label style={{ fontSize: 11, color: '#475569', display: 'block', marginBottom: 3 }}>Name</label>
              <input value={name} onChange={(e) => setName(e.target.value)} type="text"
                style={{ width: '100%', padding: '8px 10px', fontSize: 13, border: '1px solid #cbd5e1', borderRadius: 6 }} />
            </div>
            <div>
              <label style={{ fontSize: 11, color: '#475569', display: 'block', marginBottom: 3 }}>Title</label>
              <input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="CEO, COO, Board Observer…" type="text"
                style={{ width: '100%', padding: '8px 10px', fontSize: 13, border: '1px solid #cbd5e1', borderRadius: 6 }} />
            </div>
          </div>

          <div style={{ marginTop: 16, paddingTop: 16, borderTop: '1px solid #e2e8f0' }}>
            <div style={{ fontSize: 11, letterSpacing: '0.08em', textTransform: 'uppercase', color: '#94a3b8', marginBottom: 8, fontFamily: 'ui-monospace, monospace' }}>App access</div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 6, maxHeight: 240, overflowY: 'auto', padding: 4, border: '1px solid #e2e8f0', borderRadius: 6 }}>
              {(apps || []).map(a => (
                <label key={a.key} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 8px', fontSize: 13, cursor: 'pointer', background: appKeys.includes(a.key) ? '#f1f5f9' : 'transparent', borderRadius: 4 }}>
                  <input type="checkbox" checked={appKeys.includes(a.key)} onChange={() => toggleApp(a.key)} />
                  <span style={{ color: '#0f172a' }}>{a.label}</span>
                </label>
              ))}
            </div>
          </div>

          <div style={{ marginTop: 16 }}>
            <label style={{ fontSize: 11, color: '#475569', display: 'block', marginBottom: 3 }}>Access</label>
            <select value={accessRole} onChange={(e) => setAccessRole(e.target.value)}
              style={{ width: '100%', padding: '8px 10px', fontSize: 13, border: '1px solid #cbd5e1', borderRadius: 6 }}>
              <option value="viewer">Viewer (read-only)</option>
              <option value="participant">Participant (full access for granted apps)</option>
            </select>
          </div>

          <div style={{ marginTop: 12 }}>
            <label style={{ fontSize: 11, color: '#475569', display: 'block', marginBottom: 3 }}>Duration</label>
            <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
              <label style={{ fontSize: 13, display: 'flex', alignItems: 'center', gap: 6 }}>
                <input type="radio" checked={durationMode === 'temporary'} onChange={() => setDurationMode('temporary')} />
                Temporary
              </label>
              <label style={{ fontSize: 13, display: 'flex', alignItems: 'center', gap: 6 }}>
                <input type="radio" checked={durationMode === 'permanent'} onChange={() => setDurationMode('permanent')} />
                Permanent
              </label>
              {durationMode === 'temporary' && (
                <input type="date" value={expiresAt} onChange={(e) => setExpiresAt(e.target.value)}
                  style={{ padding: '6px 8px', fontSize: 12, border: '1px solid #cbd5e1', borderRadius: 6 }} />
              )}
            </div>
          </div>

          <div style={{ marginTop: 12 }}>
            <label style={{ fontSize: 11, color: '#475569', display: 'block', marginBottom: 3 }}>Delivery</label>
            <select value={deliveryMode} onChange={(e) => setDeliveryMode(e.target.value)}
              style={{ width: '100%', padding: '8px 10px', fontSize: 13, border: '1px solid #cbd5e1', borderRadius: 6 }}>
              <option value="auto">Automatic (federated for federated orgs, magic-link otherwise)</option>
              <option value="federated">Azure AD (federated)</option>
              <option value="magic_link">Magic link (email)</option>
            </select>
          </div>

          <label style={{ fontSize: 11, color: '#475569', display: 'block', marginTop: 12, marginBottom: 3 }}>Note (optional)</label>
          <textarea value={reason} onChange={(e) => setReason(e.target.value)} rows={2} maxLength={240}
            placeholder="Why this observer needs access (audit note)"
            style={{ width: '100%', padding: '8px 10px', fontSize: 13, border: '1px solid #cbd5e1', borderRadius: 6, resize: 'vertical' }} />

          <HipaaAckBanner checked={hipaaAck} onChange={setHipaaAck} />

          {error && (
            <div role="alert" style={{ marginTop: 10, fontSize: 12, color: '#b91c1c' }}>{error}</div>
          )}

          <div style={{ marginTop: 16, display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
            <button type="button" onClick={onClose} disabled={busy}
              style={{ background: 'transparent', border: '1px solid #cbd5e1', padding: '8px 14px', fontSize: 13, color: '#475569', cursor: 'pointer', borderRadius: 6 }}>Cancel</button>
            <button type="button" onClick={submit} disabled={busy || !email.trim() || appKeys.length === 0 || !hipaaAck}
              style={{ background: '#1e293b', color: '#ffffff', border: '1px solid #1e293b', padding: '8px 14px', fontSize: 13, cursor: busy ? 'wait' : 'pointer', borderRadius: 6 }}>
              {busy ? 'Sending…' : 'Send invite'}
            </button>
          </div>
        </div>
      </div>
    </>
  );
}
